/**
 * Helpers para resposta de POST /api/pagamento/v5 (GatewayPagamentoController@createPayment).
 * Sucesso = JSON bruto da order Pagar.me (sem { status: true }).
 */

export const DAILY_LIMIT_MESSAGE = 'Limite diário de pagamentos atingido.';
export const SECURITY_FORBIDDEN_MESSAGE =
  'Validação de segurança não autorizada.';
export const STUDENT_NOT_FOUND_MESSAGE = 'Cadastro aluno não identificado';

const CAPTCHA_FIELD_KEYS = ['captcha', 'captcha_id'];

const CAPTCHA_RELOAD_PATTERNS = [
  'Código de verificação expirado',
  'Código de verificação inválido',
  'Número máximo de tentativas excedido',
  'Código de verificação incorreto',
];

export function isPagarmeOrder(data) {
  if (!data || typeof data !== 'object' || Array.isArray(data)) {
    return false;
  }

  // Envelope Laravel de erro: { status: false, ... }
  if (data.status === false) {
    return false;
  }

  // Order Pagar.me: id + status string (paid, pending, failed, ...)
  return Boolean(data.id) && typeof data.status === 'string';
}

export function isCaptchaErrorMessage(message) {
  if (!message || typeof message !== 'string') {
    return false;
  }

  return (
    message.includes('Código de verificação') ||
    CAPTCHA_RELOAD_PATTERNS.some((pattern) => message.includes(pattern))
  );
}

export function shouldReloadCaptcha(message) {
  if (!message || typeof message !== 'string') {
    return false;
  }

  return CAPTCHA_RELOAD_PATTERNS.some((pattern) => message.includes(pattern));
}

/**
 * Extrai erros de validação Laravel: { error: { campo: ["msg"] } }
 * Retorna { fieldErrors: { campo: msg }, messages: string[] }
 */
export function extractValidationErrors(data) {
  const fieldErrors = {};
  const messages = [];

  if (!data?.error || typeof data.error !== 'object') {
    return { fieldErrors, messages };
  }

  Object.entries(data.error).forEach(([field, value]) => {
    let msg = null;

    if (Array.isArray(value) && value.length > 0) {
      msg = value[0];
    } else if (typeof value === 'string') {
      msg = value;
    } else if (value?.descricao) {
      msg = value.descricao;
    }

    if (msg) {
      fieldErrors[field] = msg;
      messages.push(msg);
    }
  });

  return { fieldErrors, messages };
}

export function extractCaptchaError(data) {
  if (!data) return null;

  const { fieldErrors, messages } = extractValidationErrors(data);

  for (const key of CAPTCHA_FIELD_KEYS) {
    if (fieldErrors[key]) {
      return fieldErrors[key];
    }
  }

  const fromMessages = messages.find((m) => isCaptchaErrorMessage(m));
  if (fromMessages) return fromMessages;

  if (typeof data.error === 'string' && isCaptchaErrorMessage(data.error)) {
    return data.error;
  }

  if (typeof data.message === 'string' && isCaptchaErrorMessage(data.message)) {
    return data.message;
  }

  return null;
}

export function extractPaymentErrorMessage(data, fallback) {
  const captchaError = extractCaptchaError(data);
  if (captchaError) return captchaError;

  if (typeof data?.message === 'string' && data.message) {
    return data.message;
  }

  if (typeof data?.error === 'string' && data.error) {
    return data.error;
  }

  const { messages } = extractValidationErrors(data);
  if (messages.length > 0) {
    return messages.join(' ');
  }

  return (
    fallback ||
    'Desculpe, não foi possível concluir o pagamento. Tente novamente.'
  );
}

export function isDailyLimitError(data, httpStatus) {
  if (httpStatus !== 429) return false;
  const message =
    (typeof data?.message === 'string' && data.message) ||
    (typeof data?.error === 'string' && data.error) ||
    '';
  return message.includes('Limite diário');
}

export function isThrottle429(data, httpStatus) {
  return httpStatus === 429 && !isDailyLimitError(data, httpStatus);
}

/**
 * Classifica a resposta HTTP+body do createPayment.
 * @returns {{
 *   kind: 'pagarme_order'|'validation'|'forbidden'|'daily_limit'|'throttle'|'student_not_found'|'not_found'|'technical'|'generic_error',
 *   userMessage: string,
 *   fieldErrors: Record<string,string>,
 *   captchaMessage: string|null,
 *   reloadCaptcha: boolean,
 *   reloadRecaptcha: boolean,
 *   blockDaily: boolean,
 *   order: object|null,
 * }}
 */
export function classifyPaymentV5Response({ httpStatus, data, isJson, rawText }) {
  const emptyFields = { fieldErrors: {}, captchaMessage: null };

  // 200 + order Pagar.me
  if (httpStatus === 200 && isJson && isPagarmeOrder(data)) {
    return {
      kind: 'pagarme_order',
      userMessage: null,
      ...emptyFields,
      reloadCaptcha: true, // CAPTCHA já consumido no backend
      reloadRecaptcha: false,
      blockDaily: false,
      order: data,
    };
  }

  // 200 + body não-JSON (ex.: cURL Error #:...)
  if (httpStatus === 200 && !isJson) {
    return {
      kind: 'technical',
      userMessage:
        'Não foi possível concluir o pagamento (resposta inválida do servidor). Tente novamente.',
      ...emptyFields,
      reloadCaptcha: true,
      reloadRecaptcha: true,
      blockDaily: false,
      order: null,
      rawText,
    };
  }

  // 422 validação / CAPTCHA
  if (httpStatus === 422 || (isJson && data?.status === false && data?.error)) {
    const { fieldErrors, messages } = extractValidationErrors(data);
    const captchaMessage = extractCaptchaError(data);
    const userMessage =
      messages.join(' ') ||
      extractPaymentErrorMessage(data) ||
      'Verifique os dados informados.';

    // Preferir sempre regenerar após tentativa na API (desafio pode ter sido
    // consumido ou ter tentativas limitadas). Exceção: só faltou digitar o código.
    const onlyMissingCaptcha =
      Boolean(captchaMessage) &&
      captchaMessage.includes('obrigatório') &&
      Object.keys(fieldErrors).every(
        (key) => key === 'captcha' || key === 'captcha_id'
      );

    const reloadCaptcha =
      !onlyMissingCaptcha ||
      shouldReloadCaptcha(captchaMessage || userMessage) ||
      Boolean(fieldErrors.captcha_id);

    return {
      kind: 'validation',
      userMessage,
      fieldErrors,
      captchaMessage,
      reloadCaptcha,
      reloadRecaptcha: Boolean(fieldErrors.recaptchaToken),
      blockDaily: false,
      order: null,
    };
  }

  // 403 reCAPTCHA
  if (
    httpStatus === 403 ||
    (isJson &&
      data?.status === false &&
      typeof data?.message === 'string' &&
      data.message.includes('segurança'))
  ) {
    return {
      kind: 'forbidden',
      userMessage:
        (typeof data?.message === 'string' && data.message) ||
        SECURITY_FORBIDDEN_MESSAGE,
      ...emptyFields,
      reloadCaptcha: true,
      reloadRecaptcha: true,
      blockDaily: false,
      order: null,
    };
  }

  // 429 limite diário vs throttle
  if (httpStatus === 429) {
    if (isDailyLimitError(data, httpStatus)) {
      return {
        kind: 'daily_limit',
        userMessage:
          (typeof data?.message === 'string' && data.message) ||
          DAILY_LIMIT_MESSAGE,
        ...emptyFields,
        reloadCaptcha: true,
        reloadRecaptcha: false,
        blockDaily: true,
        order: null,
      };
    }

    return {
      kind: 'throttle',
      userMessage:
        'Muitas tentativas em pouco tempo. Aguarde cerca de um minuto e tente novamente.',
      ...emptyFields,
      reloadCaptcha: true,
      reloadRecaptcha: true,
      blockDaily: false,
      order: null,
    };
  }

  // 400 aluno
  if (
    httpStatus === 400 ||
    (isJson &&
      data?.status === false &&
      typeof data?.message === 'string' &&
      data.message.toLowerCase().includes('aluno'))
  ) {
    const base =
      (typeof data?.message === 'string' && data.message) ||
      STUDENT_NOT_FOUND_MESSAGE;

    return {
      kind: 'student_not_found',
      userMessage: `${base}. Verifique seu cadastro ou entre em contato com o suporte.`,
      ...emptyFields,
      reloadCaptcha: true,
      reloadRecaptcha: true,
      blockDaily: false,
      order: null,
    };
  }

  // 404 model not found
  if (httpStatus === 404) {
    return {
      kind: 'not_found',
      userMessage:
        'Plano ou recurso inválido. Atualize a página e tente novamente.',
      ...emptyFields,
      reloadCaptcha: true,
      reloadRecaptcha: true,
      blockDaily: false,
      order: null,
    };
  }

  // Envelope status:false com message (outros)
  if (isJson && data?.status === false) {
    return {
      kind: 'generic_error',
      userMessage: extractPaymentErrorMessage(data),
      ...emptyFields,
      reloadCaptcha: true,
      reloadRecaptcha: true,
      blockDaily: false,
      order: null,
    };
  }

  // 5xx / demais
  return {
    kind: 'technical',
    userMessage:
      'Não foi possível concluir o pagamento. Tente novamente em instantes.',
    ...emptyFields,
    reloadCaptcha: true,
    reloadRecaptcha: true,
    blockDaily: false,
    order: null,
    rawText,
    httpStatus,
  };
}

export function getBoletoPdfUrl(order) {
  return order?.charges?.[0]?.last_transaction?.pdf || null;
}

export function getPixQrCodeUrl(order) {
  return order?.charges?.[0]?.last_transaction?.qr_code_url || null;
}
